// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="../../types/@/remoteConfig.d.ts" />

/* eslint-disable class-methods-use-this */
// eslint-disable-next-line import/no-unresolved
import { getRemoteConfig } from '@/remoteConfig';
import { Urls, FeatureFlags, Toggles } from './keys';
import { defaults } from './defaults';
import { Configs } from './Configs';

// Mostly just a helper class right now to munge serialized and non-serialized
// env variables
class Config {
  private get remoteConfig(): firebase.remoteConfig.RemoteConfig {
    return getRemoteConfig();
  }

  async activateRemoteConfig(): Promise<void> {
    this.remoteConfig.settings = {
      minimumFetchIntervalMillis: 3600000,
      fetchTimeoutMillis: 100,
    };

    this.remoteConfig.defaultConfig = defaults;

    await this.refresh();
  }

  toJSON(): Configs {
    return {
      authEnabled: this.authEnabled,
      authTokenGeneratorUrl: this.authTokenGeneratorUrl,
      emailValidatorUrl: this.emailValidatorUrl,
      phoneValidatorUrl: this.phoneValidatorUrl,
      testAds: this.testAds,
      coreApiUrl: this.coreApiUrl,
    };
  }

  get isReady(): boolean {
    return ['success', 'throttle'].includes(this.remoteConfig.lastFetchStatus);
  }

  async refresh(): Promise<void> {
    await this.remoteConfig.fetchAndActivate();
  }

  get authEnabled(): boolean {
    return this.remoteConfig.getBoolean(FeatureFlags.Auth);
  }

  get authTokenGeneratorUrl(): string {
    return this.remoteConfig.getString(Urls.AuthTokenGenerator);
  }

  get host(): string {
    return this.remoteConfig.getString(Urls.Host);
  }

  get emailValidatorUrl(): string {
    return this.remoteConfig.getString(Urls.EmailValidator);
  }

  get phoneValidatorUrl(): string {
    return this.remoteConfig.getString(Urls.PhoneValidator);
  }

  get coreApiUrl(): string {
    return this.remoteConfig.getString(Urls.ApiBase);
  }

  get dealerPagesApiUrl(): string {
    return this.remoteConfig.getString(Urls.DealerPagesApi);
  }

  get testAds(): boolean {
    return this.remoteConfig.getBoolean(Toggles.TestAds);
  }

  get customEnv(): string {
    return (process as any).serializedEnv.CUSTOM_ENV;
  }

  get comparisonsApiUrl(): string {
    return this.remoteConfig.getString(Urls.ComparisonsApi);
  }

  get contentApiUrl(): string {
    return this.remoteConfig.getString(Urls.ContentApi);
  }

  get lookupApiUrl(): string {
    return this.remoteConfig.getString(Urls.LookupApi);
  }

  get googleOAuthClientId(): string {
    return (process as any).serializedEnv.GOOGLE_OAUTH_CLIENT_ID;
  }

  get cognitioIdentityPoolId(): string {
    return (process as any).serializedEnv.COGNITO_IDENTITY_POOL_ID;
  }

  get cognitoRoleArn(): string {
    return (process as any).serializedEnv.COGNITO_ROLE_ARN;
  }

  get eventsKinesisStreamName(): string {
    return (process as any).serializedEnv.EVENTS_KINESIS_STREAM_NAME;
  }

  get eventsKinesisEndpoint(): string {
    return (process as any).serializedEnv.EVENTS_KINESIS_ENDPOINT;
  }

  get imageTag(): string {
    return (process as any).serializedEnv.IMAGE_TAG;
  }

  get googleAdwordsConversionIdNew(): string {
    return (process as any).serializedEnv.GOOGLE_ADWORDS_CONVERSION_ID_NEW;
  }

  get googleAdwordsConversionIdOld(): string {
    return (process as any).serializedEnv.GOOGLE_ADWORDS_CONVERSION_ID_OLD;
  }

  get newRelicBrowserMonitoringKey(): string {
    return (process as any).serializedEnv.NEW_RELIC_BROWSER_MONITORING_KEY;
  }

  get newRelicApplicationId(): string {
    return (process as any).serializedEnv.NEW_RELIC_APPLICATION_ID;
  }

  get criteoAccountId(): string {
    return (process as any).serializedEnv.CRITEO_ACCOUNT_ID;
  }

  get nodeEnv(): string {
    return (process as any).serializedEnv.NODE_ENV;
  }

  get disableEvents(): string {
    return (process as any).serializedEnv.DISABLE_EVENTS;
  }

  get debugEvents(): boolean {
    return this.remoteConfig.getBoolean(Toggles.DebugEvents);
  }

  get ravenJsDsn(): string {
    return (process as any).serializedEnv.RAVEN_JS_DSN;
  }

  get googleMapsApiKey(): string {
    return (process as any).serializedEnv.GOOGLE_MAPS_API_KEY;
  }
}

const config = new Config();

export default config;
